import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Button,
} from "reactstrap";
import { Icon } from "../../../components/Component";
import { useForm } from "react-hook-form";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";

// Titre validé manuellement (state local), pas via register
const STEP1_FIELDS = ["site_id", "type", "category", "description", "severity", "date_observation"];
const TOTAL_STEPS = 3;
const MAX_TOTAL_UPLOAD_MB = 20;
const MAX_TOTAL_UPLOAD_BYTES = MAX_TOTAL_UPLOAD_MB * 1024 * 1024;

const ObservationWizardModal = ({ isOpen, onClose, observationId, onSuccess }) => {
  const isEdit = !!observationId;
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [previewUrls, setPreviewUrls] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [titleValue, setTitleValue] = useState("");
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [copilotStatus, setCopilotStatus] = useState(null);
  const [copilotSuggestion, setCopilotSuggestion] = useState(null);
  const { register, handleSubmit, errors, setValue, trigger, reset, watch, getValues, setError, clearErrors } = useForm({
    shouldUnregister: false,
    defaultValues: {
      title: "",
      type: "positive",
      category: "",
      description: "",
      severity: "low",
      status: "submitted",
      site_id: "",
      risk_potential: "",
      equipment_concerned: "",
      people_exposed: "",
      number_of_people_exposed: "",
      exposure_duration: "",
      immediate_action_taken: false,
      improvement_proposal: "",
      correction_due_date: "",
      estimated_cost: "",
      location_latitude: "",
      location_longitude: "",
      location_description: "",
      weather_conditions: "",
      date_observation: "",
      assigned_to: "",
    },
  });

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      return;
    }
    loadSites();
    loadUsers();
    loadCopilotStatus();
    if (isEdit) {
      loadObservation();
    } else {
      setStep(1);
      setTitleValue("");
      setCopilotSuggestion(null);
      reset({
        title: "",
        type: "positive",
        category: "",
        description: "",
        severity: "low",
        status: "submitted",
        site_id: "",
        risk_potential: "",
        equipment_concerned: "",
        people_exposed: "",
        number_of_people_exposed: "",
        exposure_duration: "",
        immediate_action_taken: false,
        improvement_proposal: "",
        correction_due_date: "",
        estimated_cost: "",
        location_latitude: "",
        location_longitude: "",
        location_description: "",
        weather_conditions: "",
        date_observation: "",
        assigned_to: "",
      });
      setAttachments([]);
    }
  }, [isOpen, observationId]);

  useEffect(() => {
    const urls = {};
    attachments.forEach((file, index) => {
      if (isImage(file) || isVideo(file)) {
        urls[index] = URL.createObjectURL(file);
      }
    });
    setPreviewUrls(urls);
    return () => {
      Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [attachments]);

  const loadSites = async () => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user.sites && user.sites.length > 0) {
          setSites(user.sites);
          return;
        }
      }
      const response = await api.getSites();
      setSites(response.data || response || []);
    } catch (error) {
      try {
        const meResponse = await api.me();
        if (meResponse.sites && meResponse.sites.length > 0) setSites(meResponse.sites);
      } catch (meError) {
        toast.error("Erreur lors du chargement des sites");
      }
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.getUsers({ per_page: 100 });
      setUsers(response.data || response || []);
    } catch (error) {
      console.error("Erreur chargement utilisateurs:", error);
    }
  };

  const loadCopilotStatus = async () => {
    try {
      const status = await api.getObservationCopilotStatus();
      setCopilotStatus(status);
    } catch (error) {
      setCopilotStatus({ configured: false });
    }
  };

  const loadObservation = async () => {
    try {
      setLoading(true);
      const observation = await api.getObservation(observationId);
      const obs = observation.data || observation;
      let dateObservation = "";
      if (obs.date_observation) {
        dateObservation = new Date(obs.date_observation).toISOString().slice(0, 16);
      } else if (obs.created_at) {
        dateObservation = new Date(obs.created_at).toISOString().slice(0, 16);
      }
      let correctionDate = obs.correction_due_date
        ? new Date(obs.correction_due_date).toISOString().split("T")[0]
        : "";
      const defaults = {
        title: obs.title || "",
        type: obs.type || "positive",
        category: obs.category || "",
        description: obs.description || "",
        severity: obs.severity || "low",
        status: obs.status || "submitted",
        site_id: obs.site_id != null && obs.site_id !== "" ? String(obs.site_id) : "",
        risk_potential: obs.risk_potential || "",
        equipment_concerned: obs.equipment_concerned || "",
        people_exposed: obs.people_exposed || "",
        number_of_people_exposed: obs.number_of_people_exposed || "",
        exposure_duration: obs.exposure_duration || "",
        immediate_action_taken: obs.immediate_action_taken || false,
        improvement_proposal: obs.improvement_proposal || "",
        correction_due_date: correctionDate,
        estimated_cost: obs.estimated_cost || "",
        location_latitude: obs.location_latitude || "",
        location_longitude: obs.location_longitude || "",
        location_description: obs.location_description || "",
        weather_conditions: obs.weather_conditions || "",
        date_observation: dateObservation,
        assigned_to: (() => { const a = obs.assigned_to?.id ?? obs.assigned_to; return (a != null && a !== "") ? String(a) : ""; })(),
      };
      reset(defaults);
      setTitleValue(defaults.title || "");
    } catch (error) {
      toast.error("Erreur lors du chargement de l'observation");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    if (newFiles.length > 0) {
      const allowedExtensions = ["jpg", "jpeg", "png", "gif", "webp", "pdf", "mp4", "avi", "mov", "wmv", "flv", "webm"];
      const invalidFiles = newFiles.filter((file) => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        return !ext || !allowedExtensions.includes(ext);
      });
      if (invalidFiles.length > 0) {
        toast.warning("Certains fichiers ont été ignorés (format non autorisé).");
      }
      const validFiles = newFiles.filter((file) => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        return ext && allowedExtensions.includes(ext);
      });
      if (validFiles.length === 0) {
        e.target.value = "";
        return;
      }
      setAttachments((prev) => {
        const existing = prev.map((f) => `${f.name}-${f.size}`);
        const unique = validFiles.filter((f) => !existing.includes(`${f.name}-${f.size}`));
        const nextAttachments = [...prev, ...unique];
        const totalSize = nextAttachments.reduce((sum, file) => sum + (file.size || 0), 0);
        if (totalSize > MAX_TOTAL_UPLOAD_BYTES) {
          toast.error(`La taille totale des fichiers ne doit pas dépasser ${MAX_TOTAL_UPLOAD_MB} Mo.`);
          return prev;
        }
        return nextAttachments;
      });
    }
    e.target.value = "";
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const isImage = (file) => file.type && file.type.startsWith("image/");
  const isVideo = (file) => file.type && file.type.startsWith("video/");
  const getFilePreview = (file, index) => previewUrls[index] || null;

  const onNext = async () => {
    if (step === 1) {
      const titleVal = (titleValue ?? "").toString().trim();
      if (!titleVal) {
        setError("title", { type: "required", message: "Le titre est requis" });
        return;
      }
      setValue("title", titleVal, { shouldValidate: false });
      clearErrors("title");
      const valid = await trigger(STEP1_FIELDS);
      if (valid) setStep(2);
    } else if (step === 2) {
      setStep(3);
    }
  };

  const onBack = () => setStep((s) => s - 1);

  const applyCopilotSuggestion = (suggestion) => {
    if (!suggestion || typeof suggestion !== "object") {
      return;
    }

    if (suggestion.title) {
      setTitleValue(suggestion.title);
      setValue("title", suggestion.title, { shouldValidate: true });
    }

    const fieldKeys = ["type", "category", "severity", "risk_potential", "improvement_proposal"];
    fieldKeys.forEach((field) => {
      const value = suggestion[field];
      if (typeof value === "string" && value.trim() !== "") {
        setValue(field, value, { shouldValidate: true, shouldDirty: true });
      }
    });
  };

  const handleCopilotSuggest = async () => {
    const description = String(getValues("description") || "").trim();
    if (description.length < 20) {
      toast.warning("Ajoute au moins 20 caractères dans la description pour utiliser le copilote IA.");
      return;
    }

    setCopilotLoading(true);
    try {
      const payload = {
        title: String(titleValue || "").trim() || null,
        description,
        type: getValues("type") || null,
        category: getValues("category") || null,
        severity: getValues("severity") || null,
        risk_potential: String(getValues("risk_potential") || "").trim() || null,
        improvement_proposal: String(getValues("improvement_proposal") || "").trim() || null,
        site_id: getValues("site_id") ? parseInt(getValues("site_id"), 10) : null,
      };

      const result = await api.postObservationCopilotSuggest(payload);
      setCopilotSuggestion(result);
      applyCopilotSuggestion(result);
      toast.success("Suggestions IA appliquées au formulaire.");
    } catch (error) {
      const msg = error?.response?.data?.error || error?.message || "Copilote IA indisponible.";
      toast.error(msg);
    } finally {
      setCopilotLoading(false);
    }
  };

  const onFormSubmit = async (submitData) => {
    setValue("title", titleValue, { shouldValidate: false });
    const data = { ...getValues(), ...submitData };
    const titleTrimmed = (data.title ?? titleValue ?? "").toString().trim();
    if (!titleTrimmed) {
      setError("title", { type: "required", message: "Le titre est requis" });
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setUploadProgress(0);
      const formattedData = {
        title: titleTrimmed,
        type: data.type || "positive",
        category: data.category || "",
        description: (data.description || "").trim(),
        severity: data.severity || "low",
        status: data.status || "submitted",
        site_id: data.site_id && data.site_id !== "" ? parseInt(data.site_id, 10) : null,
        risk_potential: data.risk_potential || "",
        equipment_concerned: data.equipment_concerned || "",
        people_exposed: data.people_exposed || "",
        number_of_people_exposed: data.number_of_people_exposed ? parseInt(data.number_of_people_exposed, 10) : null,
        exposure_duration: data.exposure_duration || "",
        immediate_action_taken: data.immediate_action_taken === true || data.immediate_action_taken === "true" || data.immediate_action_taken === "1",
        improvement_proposal: data.improvement_proposal || "",
        correction_due_date: data.correction_due_date || null,
        estimated_cost: data.estimated_cost ? parseFloat(data.estimated_cost) : null,
        location_latitude: data.location_latitude ? parseFloat(data.location_latitude) : null,
        location_longitude: data.location_longitude ? parseFloat(data.location_longitude) : null,
        location_description: data.location_description || "",
        weather_conditions: data.weather_conditions || "",
        date_observation: data.date_observation || null,
        assigned_to: data.assigned_to && data.assigned_to !== "" ? parseInt(data.assigned_to, 10) : null,
      };
      if (!formattedData.site_id || isNaN(formattedData.site_id)) {
        toast.error("Le site est requis");
        setLoading(false);
        return;
      }
      if (!formattedData.title || formattedData.title.trim() === "") {
        toast.error("Le titre est requis");
        setLoading(false);
        return;
      }
      if (!formattedData.description || formattedData.description.trim() === "") {
        toast.error("La description est requise");
        setLoading(false);
        return;
      }
      if (attachments.length > 0) formattedData.attachments = attachments;

      if (isEdit) {
        await api.updateObservation(observationId, formattedData, {
          onUploadProgress: (percent) => setUploadProgress(percent),
        });
        toast.success("Observation mise à jour avec succès");
      } else {
        await api.createObservation(formattedData, {
          onUploadProgress: (percent) => setUploadProgress(percent),
        });
        toast.success("Observation créée avec succès");
      }
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      let msg = "Erreur lors de l'enregistrement";
      if (error.errors) msg = Object.values(error.errors).flat().join(", ");
      else if (error.message) msg = error.message;
      else if (error.response?.data?.message) msg = error.response.data.message;
      else if (error.response?.data?.errors) msg = Object.values(error.response.data.errors).flat().join(", ");
      toast.error(msg);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    setStep(1);
    setAttachments([]);
    setCopilotSuggestion(null);
    onClose();
  };

  const showBackButton = step > 1;
  const showNextButton = step < TOTAL_STEPS;
  const showSubmitButton = step === TOTAL_STEPS;

  const w = watch();

  return (
    <Modal isOpen={isOpen} toggle={handleClose} className="modal-dialog-centered modal-xl">
      <div style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <ModalHeader toggle={handleClose} style={{ flexShrink: 0 }}>
          <span className="text-soft me-2">Étape {step}/{TOTAL_STEPS}</span>
          {isEdit ? "Modifier l'observation" : "Nouvelle observation"}
        </ModalHeader>
        <Form onSubmit={handleSubmit(onFormSubmit)} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <ModalBody style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          {loading && isEdit ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
              <p className="mt-2 text-soft">Chargement...</p>
            </div>
          ) : (
            <>
              <div style={{ display: step === 1 ? "block" : "none" }}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Titre *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.title ? "is-invalid" : ""}`}
                      name="title"
                      value={titleValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        setTitleValue(v);
                        setValue("title", v, { shouldValidate: false });
                      }}
                    />
                    {errors.title && <div className="invalid-feedback d-block">{errors.title.message}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Site *</label>
                    <select
                      key={`site-${isEdit && !loading ? `edit-${observationId}` : "new"}`}
                      className={`form-select ${errors.site_id ? "is-invalid" : ""}`}
                      {...register("site_id", { required: "Le site est requis", validate: (v) => v !== "" || "Le site est requis" })}
                      defaultValue={getValues("site_id") != null && getValues("site_id") !== "" ? String(getValues("site_id")) : ""}
                      onChange={(e) => setValue("site_id", e.target.value, { shouldValidate: true, shouldDirty: true })}
                    >
                      <option value="">Sélectionner un site</option>
                      {sites.map((s) => (
                        <option key={s.id} value={String(s.id)}>{s.name}</option>
                      ))}
                    </select>
                    {errors.site_id && <div className="invalid-feedback d-block">{errors.site_id.message}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Type *</label>
                    <select
                      className={`form-select ${errors.type ? "is-invalid" : ""}`}
                      {...register("type", { required: "Le type est requis" })}
                      value={w?.type ?? "positive"}
                      onChange={(e) => setValue("type", e.target.value)}
                    >
                      <option value="positive">Observation positive</option>
                      <option value="dangerous">Situation dangereuse</option>
                      <option value="near_miss">Presqu'accident</option>
                      <option value="non_conformity">Non-conformité</option>
                    </select>
                    {errors.type && <div className="invalid-feedback d-block">{errors.type.message}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Catégorie</label>
                    <select
                      className="form-select"
                      {...register("category")}
                      value={w?.category ?? ""}
                      onChange={(e) => setValue("category", e.target.value)}
                    >
                      <option value="">Sélectionner une catégorie</option>
                      <option value="safety">Sécurité</option>
                      <option value="environment">Environnement</option>
                      <option value="quality">Qualité</option>
                      <option value="health">Santé</option>
                      <option value="other">Autre</option>
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Description *</label>
                    <textarea
                      className={`form-control ${errors.description ? "is-invalid" : ""}`}
                      rows="4"
                      {...register("description", { required: "La description est requise" })}
                      value={getValues("description") ?? ""}
                      onChange={(e) => setValue("description", e.target.value)}
                    />
                    {errors.description && <div className="invalid-feedback d-block">{errors.description.message}</div>}
                  </div>
                  <div className="col-12">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Button
                        type="button"
                        color="primary"
                        outline
                        size="sm"
                        onClick={handleCopilotSuggest}
                        disabled={copilotLoading || copilotStatus?.configured === false}
                      >
                        {copilotLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                            Suggestion IA...
                          </>
                        ) : (
                          <>
                            <Icon name="cpu" className="me-1" />
                            Suggérer avec IA
                          </>
                        )}
                      </Button>
                      {copilotStatus?.configured === false && (
                        <span className="text-warning small">IA non configurée côté serveur (GROQ_API_KEY).</span>
                      )}
                    </div>
                    {copilotSuggestion && (
                      <div className="alert alert-pro alert-info mt-2 mb-0">
                        <div className="fw-medium mb-1">Copilote IA appliqué</div>
                        <div className="small text-soft">
                          Confiance: {Math.round((copilotSuggestion.confidence || 0) * 100)}%
                          {copilotSuggestion.model ? ` · Modèle: ${copilotSuggestion.model}` : ""}
                        </div>
                        {Array.isArray(copilotSuggestion.probable_causes) && copilotSuggestion.probable_causes.length > 0 && (
                          <div className="mt-2">
                            <span className="small fw-medium">Causes probables:</span>
                            <ul className="mb-0 mt-1 ps-3">
                              {copilotSuggestion.probable_causes.slice(0, 3).map((item, idx) => (
                                <li key={`cause-${idx}`} className="small">{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Gravité</label>
                    <select
                      className="form-select"
                      {...register("severity")}
                      value={w?.severity ?? "low"}
                      onChange={(e) => setValue("severity", e.target.value)}
                    >
                      <option value="low">Faible</option>
                      <option value="medium">Moyenne</option>
                      <option value="high">Élevée</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Date et heure de l'observation</label>
                    <input
                      type="datetime-local"
                      className={`form-control ${errors.date_observation ? "is-invalid" : ""}`}
                      {...register("date_observation", {
                        validate: (v) => {
                          if (!v || !v.trim()) return true;
                          if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) return "Date et heure invalides";
                          return !isNaN(new Date(v).getTime()) || "Date et heure invalides";
                        },
                      })}
                      value={getValues("date_observation") ?? ""}
                      onChange={(e) => setValue("date_observation", e.target.value)}
                    />
                    {errors.date_observation && <div className="invalid-feedback d-block">{errors.date_observation.message}</div>}
                  </div>
                </div>
              </div>

              <div style={{ display: step === 2 ? "block" : "none" }}>
                <div className="row g-3">
                  <h6 className="col-12 text-soft mb-0">Contexte et risque</h6>
                  <div className="col-12">
                    <label className="form-label">Potentiel de risque</label>
                    <textarea className="form-control" rows="2" {...register("risk_potential")} value={getValues("risk_potential") ?? ""} onChange={(e) => setValue("risk_potential", e.target.value)} placeholder="Décrire le potentiel de risque..." />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Équipement concerné</label>
                    <input type="text" className="form-control" {...register("equipment_concerned")} value={getValues("equipment_concerned") ?? ""} onChange={(e) => setValue("equipment_concerned", e.target.value)} placeholder="Nom de l'équipement..." />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Conditions météorologiques</label>
                    <select
                      className="form-select"
                      {...register("weather_conditions")}
                      value={w?.weather_conditions ?? ""}
                      onChange={(e) => setValue("weather_conditions", e.target.value)}
                    >
                      <option value="">Sélectionner</option>
                      <option value="sunny">Ensoleillé</option>
                      <option value="cloudy">Nuageux</option>
                      <option value="rainy">Pluvieux</option>
                      <option value="stormy">Orageux</option>
                      <option value="windy">Venteux</option>
                      <option value="foggy">Brumeux</option>
                      <option value="dry">Sec</option>
                      <option value="humid">Humide</option>
                      <option value="other">Autre</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Personnes exposées</label>
                    <input type="text" className="form-control" {...register("people_exposed")} value={getValues("people_exposed") ?? ""} onChange={(e) => setValue("people_exposed", e.target.value)} placeholder="Décrire les personnes exposées..." />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Nombre de personnes exposées</label>
                    <input
                      type="number"
                      className={`form-control ${errors.number_of_people_exposed ? "is-invalid" : ""}`}
                      min="0"
                      placeholder="0"
                      {...register("number_of_people_exposed", {
                        validate: (v) => {
                          if (!v || v === "") return true;
                          const n = parseInt(v, 10);
                          return (!isNaN(n) && n >= 0) || "Entier positif requis";
                        },
                      })}
                      value={getValues("number_of_people_exposed") ?? ""}
                      onChange={(e) => setValue("number_of_people_exposed", e.target.value)}
                    />
                    {errors.number_of_people_exposed && <div className="invalid-feedback d-block">{errors.number_of_people_exposed.message}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Durée d'exposition</label>
                    <select
                      className="form-select"
                      {...register("exposure_duration")}
                      value={w?.exposure_duration ?? ""}
                      onChange={(e) => setValue("exposure_duration", e.target.value)}
                    >
                      <option value="">Sélectionner</option>
                      <option value="momentary">Momentanée</option>
                      <option value="few_minutes">Quelques minutes</option>
                      <option value="few_hours">Quelques heures</option>
                      <option value="full_day">Journée complète</option>
                      <option value="several_days">Plusieurs jours</option>
                      <option value="ongoing">En cours</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Assigné à</label>
                    <select
                      className="form-select"
                      {...register("assigned_to")}
                      value={getValues("assigned_to") ?? ""}
                      onChange={(e) => setValue("assigned_to", e.target.value)}
                    >
                      <option value="">Sélectionner un utilisateur</option>
                      {users.map((u) => (
                        <option key={u.id} value={String(u.id)}>{u.first_name} {u.last_name} ({u.email})</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <div className="form-check">
                      <input type="checkbox" className="form-check-input" {...register("immediate_action_taken")} checked={!!w?.immediate_action_taken} onChange={(e) => setValue("immediate_action_taken", e.target.checked)} />
                      <label className="form-check-label">Action immédiate prise</label>
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Proposition d'amélioration</label>
                    <textarea className="form-control" rows="2" {...register("improvement_proposal")} value={getValues("improvement_proposal") ?? ""} onChange={(e) => setValue("improvement_proposal", e.target.value)} placeholder="Décrire les propositions..." />
                  </div>
                </div>
              </div>

              <div style={{ display: step === 3 ? "block" : "none" }}>
                <div className="row g-3">
                  <h6 className="col-12 text-soft mb-0">Localisation, suivi et pièces jointes</h6>
                  <div className="col-md-6">
                    <label className="form-label">Localisation - Latitude</label>
                    <input
                      type="number"
                      step="any"
                      className={`form-control ${errors.location_latitude ? "is-invalid" : ""}`}
                      placeholder="Ex: -4.2634"
                      {...register("location_latitude", {
                        validate: (v) => {
                          if (!v || v === "") return true;
                          const n = parseFloat(v);
                          return (!isNaN(n) && n >= -90 && n <= 90) || "Latitude entre -90 et 90";
                        },
                      })}
                      value={getValues("location_latitude") ?? ""}
                      onChange={(e) => setValue("location_latitude", e.target.value)}
                    />
                    {errors.location_latitude && <div className="invalid-feedback d-block">{errors.location_latitude.message}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Localisation - Longitude</label>
                    <input
                      type="number"
                      step="any"
                      className={`form-control ${errors.location_longitude ? "is-invalid" : ""}`}
                      placeholder="Ex: 15.2429"
                      {...register("location_longitude", {
                        validate: (v) => {
                          if (!v || v === "") return true;
                          const n = parseFloat(v);
                          return (!isNaN(n) && n >= -180 && n <= 180) || "Longitude entre -180 et 180";
                        },
                      })}
                      value={getValues("location_longitude") ?? ""}
                      onChange={(e) => setValue("location_longitude", e.target.value)}
                    />
                    {errors.location_longitude && <div className="invalid-feedback d-block">{errors.location_longitude.message}</div>}
                  </div>
                  <div className="col-12">
                    <label className="form-label">Description de la localisation</label>
                    <input type="text" className="form-control" {...register("location_description")} value={getValues("location_description") ?? ""} onChange={(e) => setValue("location_description", e.target.value)} placeholder="Ex: Zone de production, Bâtiment A..." />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Date prévue de correction</label>
                    <input
                      type="date"
                      className={`form-control ${errors.correction_due_date ? "is-invalid" : ""}`}
                      {...register("correction_due_date", {
                        validate: (v) => !v || !v.trim() || !isNaN(new Date(v).getTime()) || "Date valide requise",
                      })}
                      value={getValues("correction_due_date") ?? ""}
                      onChange={(e) => setValue("correction_due_date", e.target.value)}
                    />
                    {errors.correction_due_date && <div className="invalid-feedback d-block">{errors.correction_due_date.message}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Coût estimé (FCFA)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={`form-control ${errors.estimated_cost ? "is-invalid" : ""}`}
                      placeholder="0.00"
                      {...register("estimated_cost", {
                        validate: (v) => !v || v === "" || (parseFloat(v) >= 0 && !isNaN(parseFloat(v))) || "Coût positif requis",
                      })}
                      value={getValues("estimated_cost") ?? ""}
                      onChange={(e) => setValue("estimated_cost", e.target.value)}
                    />
                    {errors.estimated_cost && <div className="invalid-feedback d-block">{errors.estimated_cost.message}</div>}
                  </div>
                  {isEdit && (
                    <div className="col-md-6">
                      <label className="form-label">Statut</label>
                      <select
                        className="form-select"
                        {...register("status")}
                        value={w?.status ?? "submitted"}
                        onChange={(e) => setValue("status", e.target.value)}
                      >
                        <option value="submitted">Soumise</option>
                        <option value="received">Reçue</option>
                        <option value="in_analysis">En analyse</option>
                        <option value="corrective_action_requested">Action requise</option>
                        <option value="action_completed">Action réalisée</option>
                        <option value="verified">Vérifiée</option>
                        <option value="closed">Clôturée</option>
                      </select>
                    </div>
                  )}
                  <div className="col-12">
                    <label className="form-label">Images/Vidéos (preuve)</label>
                    <div className="d-flex mb-2">
                      <input
                        type="file"
                        className="form-control me-2"
                        multiple
                        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.mp4,.avi,.mov,.wmv,.flv,.webm"
                        onChange={handleFileChange}
                        id="obs-wizard-file"
                        style={{ flex: 1 }}
                      />
                      <Button type="button" color="primary" outline onClick={() => document.getElementById("obs-wizard-file").click()}>
                        <Icon name="plus" />
                        <span>Ajouter</span>
                      </Button>
                    </div>
                    {attachments.length > 0 && (
                      <div className="row g-2 mt-1">
                        {attachments.map((file, index) => {
                          const preview = getFilePreview(file, index);
                          return (
                            <div key={index} className="col-md-4">
                              <div className="card border p-2">
                                {isImage(file) && preview ? (
                                  <img src={preview} alt="" className="img-fluid rounded" style={{ height: 80, objectFit: "cover", width: "100%" }} />
                                ) : isVideo(file) && preview ? (
                                  <video src={preview} className="w-100 rounded" style={{ maxHeight: 80 }} />
                                ) : (
                                  <div className="text-center py-2">
                                    <Icon name="file" className="text-muted" />
                                    <p className="small mb-0">{file.name}</p>
                                  </div>
                                )}
                                <div className="d-flex justify-content-between align-items-center mt-1">
                                  <small className="text-muted text-truncate" style={{ maxWidth: "70%" }}>{file.name}</small>
                                  <Button type="button" color="link" size="sm" className="p-0" onClick={() => removeAttachment(index)}>
                                    <Icon name="cross" className="text-danger" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <small className="text-muted d-block mt-2">
                      Formats acceptés: JPG, JPEG, PNG, GIF, WEBP, PDF, MP4, AVI, MOV, WMV, FLV, WEBM. Taille max: 10MB par fichier, 20MB au total.
                    </small>
                  </div>
                </div>
              </div>
            </>
          )}
        </ModalBody>
        {!loading && (
          <ModalFooter style={{ flexShrink: 0, borderTop: "1px solid #e5e9f2" }}>
            {showBackButton && (
              <Button color="light" type="button" onClick={onBack} className="me-auto">
                <Icon name="chevron-left" /> Retour
              </Button>
            )}
            {showNextButton && (
              <Button color="primary" type="button" onClick={onNext}>
                Suivant <Icon name="chevron-right" />
              </Button>
            )}
            {showSubmitButton && (
              <Button color="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    {isEdit ? "Modification..." : "Création..."}
                  </>
                ) : (
                  isEdit ? "Enregistrer" : "Créer l'observation"
                )}
              </Button>
            )}
          </ModalFooter>
        )}
        {loading && attachments.length > 0 && (
          <div className="px-4 pb-3">
            <div className="alert alert-info mb-0">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span>Téléversement des fichiers de preuve...</span>
                <strong>{uploadProgress}%</strong>
              </div>
              <div className="progress" style={{ height: "8px" }}>
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </div>
          </div>
        )}
      </Form>
      </div>
    </Modal>
  );
};

export default ObservationWizardModal;
